// VideoSection.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';

import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import R2Video from '@/components/ui/R2Video/R2Video';

interface VideoItem {
  content: string;
  styles?: Record<string, any>;
  videoUrl: string;
  description?: { content: string };
  youtubeUrl?: string;
  thumbnailPath?: string;
}

interface VideoSectionProps {
  data?: VideoItem[];
  columns?: 1 | 2 | 3;
  title?: string;
  className?: string;
  paddingclassName?: string;
  description?: { content: string };
  thumbnailPath?: string;
  youtubeUrl?: string;
}

const VideoSection: React.FC<VideoSectionProps> = ({
  data,
  columns = 3,
  title = 'Our Videos',

  paddingclassName = '',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setAnimateCards(true), 400);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      },
    );

    const currentSection = sectionRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [isMounted]);

  if (!data || !isMounted || !data.length) {
    return null;
  }

  const videos = data;

  return (
    <section
      ref={sectionRef}
      className={`bg-white transition-all duration-1000 ease-out ${paddingclassName} ${className} ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
    >
      <div className="container mx-auto max-w-6xl px-4">
        {/* Section Heading */}
        <div
          className={`mb-8 text-center transition-all duration-800 ease-out md:mb-10 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
          }`}
          style={{
            transitionDelay: '200ms',
          }}
        >
          <RichText
            className=""
            styles={{
              p: 'text-h2 text-text-heading-deep [&_b]:text-text-heading-deep [&>p]:mb-0',
            }}
          >
            {title}
          </RichText>
        </div>

        {/* Videos Grid */}
        <div
          className={`grid grid-cols-1 gap-8 transition-all duration-800 ease-out ${
            columns === 1
              ? 'mx-auto max-w-sm'
              : columns === 2
                ? 'mx-auto max-w-3xl md:grid-cols-2'
                : 'md:grid-cols-2 lg:grid-cols-3'
          } ${
            animateCards
              ? 'translate-y-0 opacity-100'
              : 'translate-y-8 opacity-0'
          }`}
          style={{
            transitionDelay: '400ms',
          }}
        >
          {videos.map((video, index) => {
            return (
              <div
                key={`${video.content}-${index}`}
                className={`group overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-200 ease-out hover:-translate-y-2 hover:shadow-xl hover:duration-150 ${
                  animateCards
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                }`}
                style={{
                  transitionDelay: animateCards
                    ? '0ms'
                    : `${600 + index * 200}ms`,
                }}
              >
                {/* Video */}
                <div className="relative overflow-hidden rounded-xl shadow-lg transition-all duration-300 group-active:shadow-xl">
                  {video.youtubeUrl ? (
                    <a
                      href={video.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative block aspect-video overflow-hidden rounded-xl"
                    >
                      <R2Image
                        path={video.thumbnailPath || ''}
                        alt={video.content}
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                      />

                      {/* Small Play Button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/70 backdrop-blur-sm">
                          <div className="ml-1 h-0 w-0 border-y-[7px] border-l-11 border-y-transparent border-l-white" />
                        </div>
                      </div>
                    </a>
                  ) : (
                    <R2Video
                      path={video.videoUrl || ''}
                      controls
                      preload="metadata"
                      poster=""
                      aspectRatio="video"
                      containerClassName="relative w-full aspect-video bg-black rounded-xl overflow-hidden"
                      className="bg-black"
                      style={{ objectFit: 'contain' }}
                    />
                  )}
                </div>

                {/* Video Title */}
                <div className="p-4">
                  <RichText
                    className=""
                    styles={{
                      p: 'text-lg font-semibold text-text-heading-deep group-hover:text-gray-900 transition-colors duration-150 ease-out [&>p]:mb-0',
                    }}
                  >
                    {video.content}
                  </RichText>
                </div>
                {video.description?.content && (
                  <div className="px-4 pb-4">
                    <RichText
                      styles={{
                        p: 'text-sm text-gray-600 ',
                      }}
                    >
                      {video.description.content}
                    </RichText>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
